import { captureException } from '@sentry/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteSiloMutation,
  useGetCurrentUserSilosQuery,
} from './Silos.generated';
import { CreateOrEditSiloForm, SilosTable } from './components';
import {
  Button,
  ConfirmDialog,
  LoadingSpinner,
  PortalModal,
} from '@web/utils/src';
import { useCustomToast } from '@/hooks/useCustomToast';
import { Silo } from '@/types';

const MyFarmPage = () => {
  const { t } = useTranslation();
  // urql doesnt invalidate on silo delete mutation
  // I guess because it returns boolean and doesnt have a typename? idk
  const [{ data, fetching }, reexecuteQuery] = useGetCurrentUserSilosQuery({
    requestPolicy: 'cache-and-network',
  });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [siloToEdit, setSiloToEdit] = useState<Silo | undefined>(undefined);
  const [siloToDelete, setSiloToDelete] = useState<Silo | undefined>(undefined);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [, deleteSiloMutation] = useDeleteSiloMutation();
  const toast = useCustomToast();

  const handleCreateNewSilo = () => {
    setSiloToEdit(undefined);
    setIsEditOpen(true);
  };
  const handleEdit = (silo: Silo) => {
    setSiloToEdit(silo);
    setIsEditOpen(true);
  };

  const handleDelete = (silo: Silo) => {
    setSiloToDelete(silo);
    setIsConfirmOpen(true);
  };

  const deleteSilo = async (silo: Silo) => {
    try {
      const res = await deleteSiloMutation({ id: silo.id });
      if (res.data?.deleteSilo) {
        reexecuteQuery();
        toast('success', t('farm.silos.silo_deleted_successfully'));
        setIsConfirmOpen(false);
        setSiloToDelete(undefined);
      } else {
        toast('error', t('farm.silos.error_deleting_silo'));
      }
    } catch (e) {
      captureException(e);
      console.error(e);
    }
  };

  return (
    <>
      <ConfirmDialog
        text={t('farm.silos.are_you_sure_you_want_to_delete')}
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        submitHandler={() => {
          if (siloToDelete) {
            deleteSilo(siloToDelete);
          }
        }}
        t={t}
      />
      <PortalModal isOpen={isEditOpen} setIsOpen={setIsEditOpen}>
        <CreateOrEditSiloForm
          isEdit={!!siloToEdit}
          silo={siloToEdit}
          setIsOpen={setIsEditOpen}
        />
      </PortalModal>
      <div className="py-6-0">
        <div>
          <div className="flex justify-between items-center">
            <p className="text-2-0 font-bd mb-2-0">{t('farm.silos.title')}</p>
            <div className="flex gap-x-2-0">
              <Button onClick={handleCreateNewSilo}>
                {t('farm.silos.add_new_silo')}
              </Button>
            </div>
          </div>
          {fetching && (
            <div className="flex justify-center items-center">
              <LoadingSpinner />
            </div>
          )}
          {data?.currentUser?.silos && (
            <SilosTable
              silos={data?.currentUser.silos}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MyFarmPage;
